import type { InfiniteData } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { getCurrentOrgId } from "@/lib/api/client";
import { patchJobCaches, refreshJobsSoon } from "@/features/jobs/model";
import { onJobEvent, registerJobNavigator } from "@/features/jobs/toasts";
import { liveEvents } from "@/lib/api/events";
import { invalidateCalendar } from "@/features/calendar/hooks";
import { apiKey } from "@/lib/api/hooks";
import { invalidate, queryClient } from "@/lib/api/query-client";
import { i18n } from "@/lib/i18n";
import { isReauth, reauthToast, sendErrorInfo, TEMPORAL_WRAPPER } from "@/lib/reauth";
import type { Comment, InboxItem, Me, Message, Page, ServerEvent } from "@/lib/types";

type InboxPage = { items: InboxItem[]; nextCursor: string | null };

/** Patch a comment everywhere it is cached (inbox lists, infinite pages, thread lists). */
export function patchCommentInCache(comment: Partial<Comment> & { id: string }) {
  const patchItems = (items: InboxItem[]) =>
    items.map((it) => (it.type === "comment" && it.comment.id === comment.id ? { ...it, comment: { ...it.comment, ...comment } } : it));
  queryClient.setQueriesData<InboxPage | InfiniteData<InboxPage>>({ queryKey: ["inbox"] }, (old) => {
    if (!old) return old;
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: patchItems(p.items) })) };
    return { ...old, items: patchItems(old.items) };
  });
  // ["comments"] holds plain pages AND infinite pages (post page list): patch both shapes (a throw here stopped every later live listener)
  const patchList = (items: Comment[]) => items.map((c) => (c.id === comment.id ? { ...c, ...comment } : c));
  queryClient.setQueriesData<Page<Comment> | InfiniteData<Page<Comment>>>({ queryKey: ["comments"] }, (old) => {
    if (!old) return old;
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: patchList(p.items) })) };
    return Array.isArray(old.items) ? { ...old, items: patchList(old.items) } : old;
  });
}

/** Replace a message (same id) in every cached message page, so a sending bubble turns sent / not sent at once. */
export function upsertMessage(message: Message) {
  queryClient.setQueriesData<InfiniteData<Page<Message>>>({ queryKey: ["messages"] }, (old) =>
    old && "pages" in old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((m) => (m.id === message.id ? { ...m, ...message } : m)) })) } : old,
  );
}

// ------------------------------------------------------------- operation / batch tracking (toasts with progress)
type OperationEvent = { operationId: string; status: string; done?: number; total?: number | null; error?: string | null; errorCode?: string | null };
type Tracked = { label: string; done?: string; onDone?: () => void; onFailed?: (e: OperationEvent) => void };
const tracked = new Map<string, Tracked>();

/** Show a progress toast for a 202 operation or batch job; SSE `operation.updated` drives it. */
export function trackOperation(id: string, t: Tracked) {
  tracked.set(id, t);
  toast.loading(t.label, { id });
}

/** Failed-operation toast text: the localized line for its error code (+ the raw reason when it says more). */
function failureLine(e: OperationEvent): string {
  // no code (legacy / non-send jobs): the raw reason, unless it is Temporal's useless wrapper text
  if (!e.errorCode) return e.error && !e.error.startsWith(TEMPORAL_WRAPPER) ? e.error : i18n.t("shell.batch.opFailed");
  const info = sendErrorInfo(e);
  return info.detail ? `${info.message} · ${info.detail}` : info.message;
}

export function onOperation(e: OperationEvent) {
  const t = tracked.get(e.operationId);
  if (!t) return;
  const progress = e.total && e.total > 1 ? ` ${e.done ?? 0}/${e.total}` : "";
  if (e.status === "succeeded") {
    toast.success(t.done ?? t.label, { id: e.operationId, description: e.total && e.total > 1 ? i18n.t("shell.batch.done", { done: e.done ?? 0, total: e.total }) : undefined });
    tracked.delete(e.operationId);
    t.onDone?.();
  } else if (e.status === "failed" || e.status === "cancelled") {
    // ALP-77: a rejected channel token is not "failed": say Instagram must be reconnected and offer Reconnect
    if (e.status === "failed" && isReauth(e)) {
      reauthToast({ id: e.operationId });
      void invalidate("accounts");
    } else toast.error(t.label, { id: e.operationId, description: e.status === "failed" ? failureLine(e) : i18n.t("shell.batch.opCancelled") });
    tracked.delete(e.operationId);
    if (e.status === "failed") t.onFailed?.(e);
  } else {
    toast.loading(`${t.label}${progress}`, { id: e.operationId, description: e.total && e.total > 1 ? i18n.t("shell.batch.running", { done: e.done ?? 0, total: e.total }) : undefined });
  }
}

/** Client-side navigation from outside React (toast actions): the router follows history + popstate. */
function goTo(href: string) {
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Calendar notifications (ALP-73): refresh the calendar caches; toast only users in the notification's audience
 * (absent = everyone in the org); high priority (crisis) stays on screen until dismissed.
 */
export function onCalendarNotification(e: Extract<ServerEvent, { type: "notification" }>, opts: { userId?: string | null } = {}) {
  void invalidateCalendar();
  const me = opts.userId !== undefined ? opts.userId : (queryClient.getQueryData<Me>(apiKey("me"))?.user.id ?? null);
  if (e.audience && (!me || !e.audience.includes(me))) return false;
  const n = e.notification;
  const action = n.href ? { label: i18n.t("calendar.toast.open"), onClick: () => goTo(n.href!) } : undefined;
  if (e.priority === "high") toast.error(n.title, { id: n.id, description: n.body ?? undefined, duration: Infinity, action, closeButton: true });
  else toast(n.title, { id: n.id, description: n.body ?? undefined, duration: 6000, action });
  return true;
}

/** Wire live events into the query cache. Mounted once by the app shell. */
export function useLiveSync() {
  const navigate = useNavigate();
  useEffect(() => {
    registerJobNavigator((href) => void navigate({ href }));
  }, [navigate]);
  useEffect(() => {
    if (!getCurrentOrgId()) return;
    void liveEvents.connect();
    const off = liveEvents.on((e) => {
      switch (e.type) {
        case "inbox.item": {
          void invalidate("inbox", "inboxCounts");
          const item = e.item as InboxItem | undefined;
          // Block in Malfak: no notification for comments from blocked people (they arrive hidden)
          if (item?.type === "comment" && !item.comment.author.blocked && !location.pathname.startsWith("/inbox")) {
            toast(i18n.t("shell.notifications.newComment", { user: item.comment.author.username }), { description: item.comment.text.slice(0, 90), duration: 3500 });
          }
          break;
        }
        case "comment.updated":
          patchCommentInCache(e.comment);
          void invalidate("inboxCounts");
          break;
        case "message.created":
          void queryClient.invalidateQueries({ queryKey: ["messages"] });
          void invalidate("inbox", "conversation");
          break;
        case "message.updated":
          // ALP-77: an outbound message went sending → sent / failed (same id): upsert it, then refetch
          upsertMessage(e.message);
          void queryClient.invalidateQueries({ queryKey: ["messages"] });
          void invalidate("inbox");
          if (e.message.status === "failed" && isReauth(e.message)) {
            reauthToast({ id: `message-${e.message.id}` });
            void invalidate("accounts");
          }
          break;
        case "approval.created":
          void invalidate("approvals", "inboxCounts");
          // ALP-74/77: the approved action could not run because the account must be reconnected
          if (e.approval.execution?.status === "failed" && isReauth(e.approval.execution)) {
            reauthToast({ id: `approval-${e.approval.id}` });
            void invalidate("accounts");
          }
          break;
        case "operation.updated":
          // jobs center (ALP-67): patch job caches, refetch ETA/controls debounced, start/finish toasts (not for trackOperation ones)
          patchJobCaches(queryClient, e);
          refreshJobsSoon(queryClient, e.operationId);
          onJobEvent(e, { tracked: tracked.has(e.operationId) });
          onOperation(e);
          if (e.status === "succeeded") void invalidate("inbox", "inboxCounts", "comments", "accounts");
          else if (e.status === "failed" && isReauth(e)) void invalidate("accounts"); // the worker marked it needs_reauth
          break;
        case "publication.updated":
          void invalidate("posts", "post");
          break;
        case "notification":
          void invalidate("notifications");
          if (e.notification.kind.startsWith("calendar_")) onCalendarNotification(e);
          break;
      }
    });
    return () => {
      off();
      liveEvents.disconnect();
    };
  }, []);
}
